// "Continua de onde parou" na Central de Atendimento: o que a pessoa estava
// fazendo em cada conversa sobrevive a ir para outra página e a um F5.
//
// - Rascunho (texto, modo Mensagem/Nota, mensagem citada): localStorage, por
//   usuário + conversa, expira em 7 dias — como o rascunho do WhatsApp, só
//   some ao enviar ou apagar.
// - Aba ativa e posição da rolagem: sessionStorage (vale enquanto a aba do
//   navegador estiver aberta, igual aos filtros da fila).
//
// Toda leitura/escrita é tolerante a falha (modo privado, storage cheio).

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DRAFT_EVENT = 'sophia:draft-changed';

export interface ConversationDraft<Q = unknown> {
  text: string;
  mode?: 'reply' | 'note';
  replyingTo?: Q | null;
  /** Nome do contato — a barra de abertos mostra a conversa sem buscar nada. */
  label?: string;
  updatedAt: number;
}

function currentUserKey(): string {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return 'anon';
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.user_id ?? 'anon');
  } catch {
    return 'anon';
  }
}

const draftKey = (scope: string) => `sophia:draft:${currentUserKey()}:${scope}`;

export function loadDraft<Q = unknown>(scope: string): ConversationDraft<Q> | null {
  try {
    const raw = localStorage.getItem(draftKey(scope));
    if (!raw) return null;
    const draft = JSON.parse(raw) as ConversationDraft<Q>;
    if (!draft.updatedAt || Date.now() - draft.updatedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(scope));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

/** Grava (ou apaga, se não sobrou nada) o rascunho de uma conversa. */
export function saveDraft<Q = unknown>(scope: string, draft: Omit<ConversationDraft<Q>, 'updatedAt'>): void {
  try {
    const empty = !draft.text.trim() && !draft.replyingTo;
    const key = draftKey(scope);
    const had = localStorage.getItem(key) !== null;
    if (empty) {
      if (!had) return;
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify({ ...draft, updatedAt: Date.now() }));
    }
    window.dispatchEvent(new CustomEvent(DRAFT_EVENT, { detail: scope }));
  } catch {
    /* storage indisponível — segue sem rascunho */
  }
}

/** Rascunhos do usuário logado (barra de abertos), mais recente primeiro. */
export function listDrafts(): { scope: string; draft: ConversationDraft }[] {
  const prefix = draftKey('');
  const out: { scope: string; draft: ConversationDraft }[] = [];
  try {
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(prefix)) continue;
      const scope = key.slice(prefix.length);
      const draft = loadDraft(scope);
      if (draft && (draft.text.trim() || draft.replyingTo)) out.push({ scope, draft });
    }
  } catch { /* storage indisponível */ }
  return out.sort((a, b) => b.draft.updatedAt - a.draft.updatedAt);
}

/** Descarta o rascunho de uma conversa (fechou a aba e confirmou). */
export function discardDraft(scope: string): void {
  try {
    localStorage.removeItem(draftKey(scope));
    window.dispatchEvent(new CustomEvent(DRAFT_EVENT, { detail: scope }));
  } catch { /* ignora */ }
}

/** Texto do rascunho da conversa de WhatsApp, para a lista de contatos. */
export function draftPreview(phone: string): string | null {
  const draft = loadDraft(`wa:${phone}`);
  const text = draft?.text.trim();
  return text ? text : null;
}

export function onDraftChange(handler: () => void): () => void {
  window.addEventListener(DRAFT_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(DRAFT_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

// ── Aba ativa e rolagem (sessionStorage) ─────────────────────────────────

function readSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(`sophia:${key}`);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, value: unknown): void {
  try {
    if (value == null) sessionStorage.removeItem(`sophia:${key}`);
    else sessionStorage.setItem(`sophia:${key}`, JSON.stringify(value));
  } catch {
    /* ignora */
  }
}

export type ConversationTab = 'whatsapp' | 'manual';

export const loadActiveTab = (contactKey: string) =>
  readSession<ConversationTab>(`atendimento:tab:${contactKey}`);
export const saveActiveTab = (contactKey: string, tab: ConversationTab) =>
  writeSession(`atendimento:tab:${contactKey}`, tab);

/** Onde a pessoa parou de ler: a mensagem no topo da tela e o deslocamento.
 *  null = estava no fim da conversa. */
export interface ScrollAnchor { messageId: string; offset: number }

export const loadScrollAnchor = (phone: string) =>
  readSession<ScrollAnchor>(`atendimento:scroll:${phone}`);
export const saveScrollAnchor = (phone: string, anchor: ScrollAnchor | null) =>
  writeSession(`atendimento:scroll:${phone}`, anchor);
